import { 
    Box, Card, CardContent, Grid, InputAdornment, Paper, Table, 
    TableBody, TableCell, TableContainer, TableHead, TableRow, 
    TextField, Typography, useMediaQuery, useTheme, Chip, 
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions
} from "@mui/material";
import { StyledTableCell, StyledTableRow } from "Pages/AdminList/ProfessionalUserList";
import { useEffect, useState, useMemo } from "react";
import SearchIcon from '@mui/icons-material/Search';
import WalletIcon from '@mui/icons-material/AccountBalanceWallet';
import StarIcon from '@mui/icons-material/Stars';
import HistoryIcon from '@mui/icons-material/History';
import ColorData from "Components/CstmTheme/ColorData";
import PaymentApi from "Service/Payment.api";
import { useDispatch, useSelector } from "react-redux";
import { setAlertData, startLoading, stopLoading } from "../../Redux/Screen/Action"; 
import RedeemIcon from '@mui/icons-material/Redeem';
interface RewardHistoryInterface {
    date: string;
    desc: string;
    amount: number;
    points: number;
    caseTypeName: string;
    orderTypeName: string;
    caseId: number;
    paymentDate: string | null;
    status: string;
}

const REWARD_STATUS_MAP: Record<number, { label: string; color: string; isDeduction: boolean }> = {
    0: { label: "Earned", color: "#2e7d32", isDeduction: false },   // Earned
    1: { label: "Spent", color: "#d32f2f", isDeduction: true },    // Spent
    2: { label: "Redeemed", color: "#d32f2f", isDeduction: true }, // Redeemed
    3: { label: "Refunded", color: "#0288d1", isDeduction: false },// Refunded
    4: { label: "Approved", color: "#2e7d32", isDeduction: false },// Approved
    5: { label: "Rejected", color: "#757575", isDeduction: false },// Rejected
    6: { label: "Pending", color: "#ef6c00", isDeduction: false }, // Pending
};

const RewardsHistory: React.FC = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const dispatch = useDispatch();
    
    const [searchQuery, setSearchQuery] = useState("");
    const [rewards, setRewards] = useState<RewardHistoryInterface[]>([]);

    // 1. Add state at the top of your component
    const [isRedeemModalOpen, setIsRedeemModalOpen] = useState(false);
    
    // Get user info from Redux
    const { myInfo } = useSelector((state: any) => state?.user);

    useEffect(() => {
        fetchRewards();
    }, [myInfo?.id]);

    const fetchRewards = async () => {
        if (!myInfo?.id) return;
        
        dispatch(startLoading({ loading: true, loadtext: "Fetching rewards..." }));
        try {
            const response = await PaymentApi.getRewardHistory(myInfo.id);
            if (response.success) {
                const data = response.data;
                setRewards(data || []);
            }
        } catch (error) {
            console.error("Failed to fetch rewards:", error);
        } finally {
            dispatch(stopLoading());
        }
    };

    // --- Calculations for Summary Cards ---
    const summary = useMemo(() => {
        return rewards.reduce((acc, curr) => {
            if (curr.amount > 0) {
            acc.totalSpend += curr.amount;
        }
            acc.totalPoints += curr.points;
            return acc;
        }, { totalSpend: 0, totalPoints: 0 });
    }, [rewards]);

    // --- Filtering Logic ---
    const filteredRewards = useMemo(() => {
        const query = searchQuery.toLowerCase().trim();
        if (!query) return rewards;
        return rewards.filter(r => 
            r.desc.toLowerCase().includes(query) ||
            r.caseTypeName.toLowerCase().includes(query) ||
            r.amount.toString().includes(query) ||
            r.caseId.toString().includes(query)
        );
    }, [searchQuery, rewards]);

    const renderSummaryCards = () => (
        <Grid container spacing={3} mb={4}>
            <Grid sx={{ xs: 12, sm: 6 }}>
                <Card sx={{ borderRadius: 3, boxShadow: 3, bgcolor: '#f0f4ff' }}>
                    <CardContent sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: ColorData.primary.main, color: 'white', mr: 2 }}>
                            <WalletIcon fontSize="large" />
                        </Box>
                        <Box>
                            <Typography variant="subtitle2" color="text.primary">Net Lifetime Spend</Typography>
                            <Typography variant="h5" fontWeight="bold">₹{summary.totalSpend.toLocaleString()}</Typography>
                        </Box>
                    </CardContent>
                </Card>
            </Grid>
            <Grid sx={{ xs: 12, sm: 6 }}>
                <Card sx={{ borderRadius: 3, boxShadow: 3, bgcolor: '#e6e6ffff', border: '1px solid #7f71faff' }}>
                    <CardContent sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: '#7bce7f', color: '#333', mr: 2 }}>
                            <StarIcon fontSize="large" />
                        </Box>
                        <Box>
                            <Typography variant="subtitle2" color="text.primary">Current Reward Points</Typography>
                            <Typography variant="h5" fontWeight="bold" sx={{ color: '#7bce7f' }}>{summary.totalPoints}</Typography>
                        </Box>
                    </CardContent>
                </Card>
            </Grid>
            {/* add redeem buttomon */}
            <Grid sx={{ xs: 12, sm: 6 }}>
                <Box display="flex" flexDirection="column" gap={1.5}>
                    <Button
                        variant="contained"
                        color="secondary"
                        startIcon={<RedeemIcon />}
                        onClick={() => setIsRedeemModalOpen(true)}
                        disabled={summary.totalPoints < 500}
                        sx={{
                            fontWeight: 'bold',
                            borderRadius: 2,
                            width: 'fit-content',
                            px: 3,
                            boxShadow: 3
                        }}
                    >
                        Redeem Points
                    </Button>

                    {summary.totalPoints < 500 ? (
                        <Box>
                            <Typography variant="caption" sx={{ color: 'warning.main', fontWeight: 'medium', display: 'block', mb: 0.5 }}>
                                🔒 You need {500 - summary.totalPoints} more points to unlock redemption.
                            </Typography>
                            {/* Optional: Add a small progress bar */}
                            <Box sx={{ width: '200px', height: 6, bgcolor: 'grey.200', borderRadius: 5, overflow: 'hidden' }}>
                                <Box sx={{
                                    width: `${(summary.totalPoints / 500) * 100}%`,
                                    height: '100%',
                                    bgcolor: 'green',
                                    transition: 'width 0.5s ease-in-out'
                                }} />
                            </Box>
                        </Box>
                    ) : (
                        <Typography variant="caption" color="success.main" sx={{ fontWeight: 'medium' }}>
                            ✅ You are eligible to redeem your points now!
                        </Typography>
                    )}

                    <Typography variant="body2" color="text.primary" sx={{ fontSize: '0.75rem', maxWidth: '300px', lineHeight: 1.4 }}>
                        Points can be redeemed once you reach the <b>500 points milestone</b>.
                        Use them against specific share trading values.
                    </Typography>
                </Box>
            </Grid>
        </Grid>
    );

    // This function is called when the user clicks "Submit Request" inside the Modal
    const handleRedeemRequest = async () => {
        dispatch(startLoading({ loading: true, loadtext: "Submitting request..." }));
        try {
            if (!myInfo?.id) return;

            const response = await PaymentApi.postRedemption({
                userId: myInfo.id,
                points: summary.totalPoints
            });

            if (response.success) {
                dispatch(setAlertData({
                    alertMessage: "Request submitted! Admin will contact you soon.",
                    alertVariant: "success",
                    openAlert: true,
                }));
                fetchRewards();
            }
        } catch (error) {
            dispatch(setAlertData({
                alertMessage: "Redemption failed. Please try again.",
                alertVariant: "error",
                openAlert: true,
            }));
        } finally {
            dispatch(stopLoading());
        }
    };

    return (
        <Box p={3} width="100%" sx={{
            minHeight: '500px',
            overflowY: 'auto',
            height: '100vh',
        }}>
            <Typography variant="h5" fontWeight="bold" gutterBottom sx={{
                padding: 2, borderRadius: 2,
                backgroundColor: ColorData.primary.main,
                color: ColorData.text.secondary,
                display: 'flex', alignItems: 'center', gap: 1
            }}>
                <HistoryIcon /> Reward & Loyalty History
            </Typography>

            {renderSummaryCards()}

            <TextField
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search rewards history..."
                size="small"
                fullWidth={isMobile}
                sx={{ mb: 3, width: isMobile ? '100%' : '350px' }}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <SearchIcon />
                        </InputAdornment>
                    ),
                }}
            />

            <TableContainer
                component={Paper}
                sx={{
                    borderRadius: 3,
                    boxShadow: 2,
                    width: '100%',
                    overflowX: 'auto',
                    maxHeight: '50vh',
                    '&::-webkit-scrollbar': {
                        height: '6px',
                        width: '6px',
                    },
                    '&::-webkit-scrollbar-thumb': {
                        backgroundColor: '#ccc',
                        borderRadius: '10px',
                    }
                }}
            >
                <Table
                    stickyHeader
                    sx={{
                        minWidth: 700,
                        tableLayout: 'auto'
                    }}
                >
                    <TableHead>
                        <TableRow>
                            <StyledTableCell>Date</StyledTableCell>
                            <StyledTableCell>Case ID</StyledTableCell>
                            <StyledTableCell>Description</StyledTableCell>
                            <StyledTableCell>Amount</StyledTableCell>
                            <StyledTableCell>Points</StyledTableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredRewards.map((row, index) => {
                            const statusConfig = REWARD_STATUS_MAP[Number(row.status)] || {
                                label: "Unknown",
                                color: "#757575",
                                isDeduction: false
                            };
                            const isDeduction = statusConfig.isDeduction;
                            const isEarned = !isDeduction && Number(row.status) !== 6; // Not a deduction and not pending

                            return (
                                <StyledTableRow key={index}>
                                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{row.date}</TableCell>
                                    <TableCell>{row.caseId || "N/A"}</TableCell>
                                    <TableCell sx={{ minWidth: '200px' }}>
                                        <Box display="flex" alignItems="center" gap={1}>
                                            <Typography variant="body2" fontWeight="bold">
                                                {row.desc}
                                            </Typography>
                                            {/* Status tag for better clarity */}
                                            <Chip
                                                label={statusConfig.label}
                                                size="small"
                                                variant="outlined"
                                                sx={{
                                                    fontSize: '10px',
                                                    height: '18px',
                                                    color: statusConfig.color,
                                                    borderColor: statusConfig.color
                                                }}
                                            />
                                        </Box>
                                        <Typography variant="caption" color="text.primary">
                                            {row.caseTypeName}
                                        </Typography>
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>
                                        ₹{row.amount}
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={`${isDeduction ? '-' : '+'}${Math.abs(row.points)}`}
                                            size="small"
                                            sx={{
                                                fontWeight: 'bold',
                                                bgcolor: isDeduction ? '#ffebee' : '#e8f5e9',
                                                color: statusConfig.color,
                                                border: `1px solid ${statusConfig.color}`
                                            }}
                                        />
                                    </TableCell>
                                </StyledTableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>

            <Dialog
                open={isRedeemModalOpen}
                onClose={() => setIsRedeemModalOpen(false)}
                PaperProps={{ sx: { borderRadius: 3, p: 1 } }}
            >
                <DialogTitle sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <StarIcon sx={{ color: ColorData.primary.main }} /> Confirm Redemption
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body1" gutterBottom>
                        You are about to redeem <strong>{summary.totalPoints} points</strong>.
                    </Typography>
                    <Typography variant="body2" color="text.primary" sx={{ mt: 2, bgcolor: '#f5f5f5', p: 2, borderRadius: 2, borderLeft: `4px solid ${ColorData.primary.main}` }}>
                        <strong>Note:</strong> These points will be converted into <strong>shares</strong>.
                        Once submitted, an admin will review your request and contact you directly for further processing.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ p: 2, gap: 1 }}>
                    <Button
                        onClick={() => setIsRedeemModalOpen(false)}
                        variant="outlined"
                        color="inherit"
                        sx={{ borderRadius: 2 }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={() => {
                            setIsRedeemModalOpen(false);
                            handleRedeemRequest();
                        }}
                        variant="contained"
                        color="primary"
                        sx={{ borderRadius: 2, px: 3 }}
                    >
                        Submit Request
                    </Button>
                </DialogActions>
            </Dialog>
            
        </Box>
    );
};

export default RewardsHistory;